import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import {ContextMenu} from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  focusCellByDataId,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex,
  unfocusAllCells } from "components/table/table-automation"
import { TextEdit, DropdownEdit, CheckboxInput } from "components/table/inputs"
import { DropdownField } from "components/table/display"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
// Controls
import Controls from "components/line-items/controls/"
import NotesControls from "components/notes/"
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import lineItemsService, { LineItem } from "data/line-items"
import keyService, * as chord from "data/key"
import propertyService from "data/property"
import { handleReferences } from "data/lib/handle-references"
import { copy, paste, canPaste, getPasteInfo } from "data/lib/copy-paste"
import toggles from "feature-toggles"
// Styles
import { LineItemsTable_s } from "components/line-items/line-items-styles"

export type ContextRow = LineItem

type PropTypes = {
  tableId: string
  data: LineItem[]
  defaults: object
  hasAsset: boolean
  inflationData: object[]
  areaMeasuresData: object[]
  lineItemsData: object[]
  systemLineItemsData: object[]
  rentrollData: object[]
  type: string
  litype?: "building_capital" | "expense_non_recov" | "expense" | "misc_income"
  selectedState: [ContextRow[], (selected: ContextRow[]) => void]
  flags: {deleteFlag: number, pasteFlag: number}
}


const LineItemsTable = (props: PropTypes) => {
  const [selected, setSelected] = props.selectedState

  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      const lis = await lineItemsService.copy(contextRow.propertyid, contextRow.lineitemid, contextRow.code)
      !_.isEmpty(selected) && setSelected(selected.concat(lis as ContextRow[]))
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const lineitems = await lineItemsService.copies(contextRow.propertyid, contextRow.lineitemid, n)
        !_.isEmpty(selected) && setSelected(selected.concat(lineitems as ContextRow[]))
      })
    }},
    {label: selected.length ? 'Delete Rows' : 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if(contextRow.systeminput) return;
      const pager         = getPagingInfo(props.tableId);
      const currentRowNum = getFocusedRowIndex() + 1
      const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
      showDeleteConfirmation(contextRow, currentRecordIndex + 1)
    }},
    {label: 'Trace', icon: 'pi pi-fw pi-search', command: e=> {
      const refreshFn = () => lineItemsService.fetchLineItems(contextRow.propertyid)
      handleReferences('lineitem', contextRow.lineitemid, {refreshFn});
    }}
  ]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({lineitemid: 0, propertyid: 0, code: '',   parentlineitemid: null, type: 'building_capital', name: '', systeminput: null})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const inflationData: object[] = props.inflationData
  const areaMeasuresData: object[] = props.areaMeasuresData
  const lineItemsData: object[] = props.lineItemsData
  const systemLineItemsData: object[] = props.systemLineItemsData
  const rentrollData: object[] = props.rentrollData
  const defaults: object = props.defaults
  const field = _field('lineitem')
  const propType = props.type
  const LineItemTypeOptions = propType === "HOSPITALITY" ?
  [
    {value: "hosp_undist_expense", label: "Undistributed Expense", prefix: "Undistributed"},
    {value: "hosp_mgmt_fee", label: "Management Fee", prefix: "Mgmt Fee"},
    {value: "hosp_fixed_expense", label: "Fixed Expense", prefix: "Fixed"},
    {value: "hosp_ffe_expense", label: "FF&E Expense", prefix: "FF&E"},
    {value: "building_capital", label: "Building Capital", prefix: "Capital"}
  ]
  : propType === "COMMERCIAL" ?
  [
    {value: "building_capital", label: "Building Capital", prefix: "Capital"},
    {value: "expense", label: "Expense", prefix: "Expense"},
    {value: "expense_non_recov", label: "Expense: Non-Recov", prefix: "Expense NR"},
    {value: "misc_income", label: "Misc. Income", prefix: "Misc Inc"}
  ]
  :
  [
    {value: "building_capital", label: "Building Capital", prefix: "Capital"},
    {value: "expense", label: "Expense", prefix: "Expense"},
    {value: "misc_income", label: "Misc. Income", prefix: "Misc Inc"}
  ]

  let detail_controls = {
    basic: {control: Controls.Amount, name: "Amount"},
    amtArea: {control: Controls.AmtPerArea, name: "Amt/Area"},
    percentEGR: {control: Controls.PercentEGR, name: "% EGR"},
    percentLineItem: {control: Controls.PercentLine, name: "% of Line"},
    percentTenantRevenue: {control: Controls.PercentTenantRevenue, name: "% Tenant Revenue"},
    detailed: {control: Controls.Detailed, name: "Detailed"},
    detailedPerArea: {control: Controls.DetailedPerArea, name: "Detailed Per Area"},
    detailedPercentLineItem: {control: Controls.DetailedPercentLine, name: "Detailed % of Line"},
    prop13Tax: {control: Controls.Prop13Tax, name: "Prop 13 Tax"}
  }

  let detail_hovers = {
    basic: [{key: 'amt', label: 'Amt'}, {key: 'amttype', label: 'Amt Type'}, {key: ['inflationdetail', 'inflationNamedItemId'], label: 'Inflation'}],
    amtArea: [{key: 'amt', label: 'Amt'}, {key: 'amttype', label: 'Amt Type'}, {key: ['inflationdetail', 'inflationNamedItemId'], label: 'Inflation'}, {key: ['areameasuredetail', 'areaid'], label: 'Area Measure'}],
    percentEGR: [{key: 'amt', label: 'Percent'}],
    percentLineItem: {multiple: [{key: 'lineItemId', label: 'Account #'}, {key: 'percent', label: 'Percent'}]},
    percentTenantRevenue: {
      single: [{key: ['lineitemdetail', 'percent'], label: 'Percent'}, {key: ['lineitemdetail', 'revenue'], label: 'Revenue'}],
      multiple: [{key: 'rentRollId', label: 'Tenant'}]
    },
    lineItemDetailed: {single: [{key: ['inflationdetail', 'inflationNamedItemId'], label: 'Inflation'}]},
    detailedPerArea: {single: [{key: ['inflationdetail', 'inflationNamedItemId'], label: 'Inflation'}, {key: ['areaMeasureNamedItemId'], label: 'Area Measure'}]},
    detailedPercentLineItem: {single: [{key: 'lineItemId', label: 'Account #'}]},
    prop13Tax: [{key: ['lineitemdetail', 'year1AmtType'], label: 'Year 1 Amt Type'}, {key: ['lineitemdetail', 'year1Amt'], label: 'Amt'}, {key: ['lineitemdetail', 'percent'], label: '% of Value'}, {key: ['lineitemdetail', 'valuationScenario'], label: 'Valuation Scenario'}, {key: ['lineitemdetail', 'inflationNamedItemId'], label: 'Inflation'}]
  }

  if(props.type !== "COMMERCIAL" && _.get(detail_controls, "percentTenantRevenue")) {
    // @ts-ignore TS2790
    delete detail_controls.percentTenantRevenue
    // @ts-ignore TS2790
    delete detail_hovers.percentTenantRevenue
  }

  if(props.type === "HOSPITALITY" && _.get(detail_controls, "percentEGR")) {
    // @ts-ignore TS2790
    delete detail_controls.percentEGR
    // @ts-ignore TS2790
    delete detail_hovers.percentEGR
  }

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const lineItemsEventSubscription = lineItemsService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "LINEITEM_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
        case e.type === "LINEITEM_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code)
          }, 500)
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            let defaults = {...props.defaults}
            if (props.litype) _.assign(defaults, {type: props.litype})
            lineItemsService.create(propertyService.getStoredPropertyId(), defaults, props.type)
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[currentRecordIndex];
            if (!currentRow.systeminput) showDeleteConfirmation(currentRow, currentRecordIndex + 1)
          }
          break
      }
    })

    return function cleanup() {
      carouselSubscription.unsubscribe()
      lineItemsEventSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [props.litype])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    setMenu(nonSystemMenu)
  }, [contextRow])

  const persist = (value, field, rowData, isPaste) => {
    let lineItem = _.omit(_.cloneDeep(rowData), 'index') // strip fields added for table view

    // lineitemdetail passes the whole record back as the value because it
    // needs to update multiple database columns due to legacy schema design
    if(!isPaste && field === "lineitemdetail" && !_.isEqual(value, {state: "none"})) {
      lineItem = value
    } else {
      _.set(lineItem, field, value)
    }

    lineItemsService.patch(lineItem)
    if (value && field === "lineitemdetail") carouselService.updateRowData(lineItem);
  }

  const refresh = async () => {
    const { propertyid } = contextRow
    const lineitems = await lineItemsService.fetchLineItems(propertyid)
    setSelected(_.intersectionBy(lineitems, selected, 'lineitemid'))
  }

  useEffect(() => {
    if(canPaste(contextCell)) {
      nonSystemMenu.splice(1, 0, {label: selected.length ? 'Paste to Rows' : 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => {
        if (selected.length) {
          showPasteConfirmation(contextRow, -1)
          return
        }
        paste(contextCell, contextRow, refresh, 'lineitem', selected)
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu([]) : setMenu(nonSystemMenu)
  }, [contextCell, selected.length])

  useEffect(() => {
    if (!props.flags.deleteFlag) return //skip first time
    showDeleteConfirmation(_.first(selected), -1)
  }, [props.flags.deleteFlag])

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      message: `Would you like to delete the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        if (selected.length > 1) {
          lineItemsService.deleteMultiple(record.propertyid, _.map(selected, 'lineitemid'))
        } else {
          lineItemsService.deleteOne(record.propertyid, record.lineitemid)
        }
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      code: record.code,
      selected
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.lineitemid)}}
        copy={copy}
        show={true}
        title={contextRow.code}
      />
    )
  }

  useEffect(() => {
    if (!props.flags.pasteFlag) return //skip first time
    if (!canPaste(contextCell, selected)) return
    showPasteConfirmation(contextRow, -1)
  }, [props.flags.pasteFlag])

  const showPasteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      title: 'Paste',
      message: `Would you like to paste ${getPasteInfo()._meta.field} data to the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        paste(contextCell, contextRow, refresh, 'lineitem', selected)
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      code: record.code,
      selected
    }))
  }

  return (
    <div className="line-items-table-container">
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      { defaults &&
      <LineItemsTable_s id={`${props.tableId}-scrollable-container`}>
        {inflationData.length > 0 && areaMeasuresData.length > 0 && lineItemsData && systemLineItemsData &&
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={props.data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth={toggles.multiselect() ? "520px" : "475px"}
          paginatorPosition="bottom"
          onContextMenu={e => menuRef.current ? _.invoke(menuRef.current, "show", e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setSelected([])
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
          selection={selected} onSelectionChange={(e) => {
            const selected = _.filter(e.value, (it) => it.systeminput !== "systemImmutable")
            setSelected(selected)
          }}
        >
          {toggles.multiselect() && <Column selectionMode="multiple" frozen={true} style={{minWidth: "45px", paddingLeft: "8px"}}/>}
          <Column header="#" frozen={true} style={{width: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "180px", maxWidth: "180px"}} frozen={true} className="p-editable-column"
            header={props.hasAsset ? "Acct #" : "Code"}
            {...field('code')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px", maxWidth: "250px"}} frozen={true} className="p-editable-column"
            header="Name"
            {...field('name')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "200px", maxWidth: "200px"}} className="p-editable-column"
            filter
            header="Line Item Type"
            {...field('type', (rowData, props) => DropdownField({options: LineItemTypeOptions, default: "expense", rowData, persist, ...props}))}
            editor={props => <DropdownEdit options={LineItemTypeOptions} default="expense" persist={persist} {...props} />}
          />
          <Column style={{minWidth: "100px", maxWidth: "100px"}} className="p-editable-column"
            header="Cash Flow?"
            {...field('reportstatus', (rowData, props) => CheckboxInput({persist, ...props}))}
            editor={props => CheckboxInput({persist, ...props})}
          />
          <Column style={{minWidth: "200px", maxWidth: "200px"}} className="p-editable-column"
            header="Input Type"
            {...field('lineitemdetail', (rowData, props) =>
              <CarouselTrigger field="lineitemdetail" title="Input Type" rowData={rowData} persist={persist} defaults={defaults}
                controls={detail_controls}
                componentAtRest={Controls.InputTypeAtRest}
                hover={detail_hovers}
                namedItems={{
                  inflation: inflationData,
                  areaMeasures: areaMeasuresData,
                  lineItems: lineItemsData,
                  systemLineItems: systemLineItemsData,
                  rentroll: rentrollData,
                  valuationItems: _.get(propertyService.get(), 'valuationdetail', []),
                  LineItemTypeOptions,
                  propType
                }}
              />,
              (err) => _.includes(['amt', 'amttype', 'areaid', 'lineitemdetail', 'inflationdetail', 'inputtype'], err.path[0]))
            }
          />
          <Column style={{minWidth: "70px", maxWidth: "70px"}} className="p-editable-column"
            header="Cap"
            {...field('capdetail', (rowData, props) =>
              <CarouselTrigger field="capdetail" rowData={rowData} title="Cap" persist={persist}
                controls={{
                  basic: {control: Controls.Cap, name: "Cap"}
                }}
                namedItems={{inflation: inflationData}}
              />
            )}
          />
          <Column style={{minWidth: "80px", maxWidth: "80px"}} className="p-editable-column"
            header="Floor"
            {...field('floordetail', (rowData, props) =>
              <CarouselTrigger field="floordetail" rowData={rowData} title="Floor" persist={persist}
                controls={{
                  basic: {control: Controls.Floor, name: "Floor"}
                }}
                namedItems={{inflation: inflationData}}
              />
            )}
          />
          <Column style={{minWidth: "150px", maxWidth: "150px"}} className="p-editable-column"
            header="Notes"
            {...field('notedetail', (rowData, props) =>
              <CarouselTrigger
                field="notedetail"
                rowData={rowData}
                title='Line Items'
                persist={persist}
                controls={{single: {control: NotesControls.NotesEdit, name: "Notes"}}}
                componentAtRest={NotesControls.NotesDisplay}
              />
            )}
          />
        </DataTable> }
        { carouselData && carouselData['show'] && <CarouselModal code="code" data={carouselData} key="carousel-modal" width={887} /> }
        { deleteConfirmation }
        { duplicateConfirmation }
        { inflationData.length > 0 && areaMeasuresData.length > 0 && lineItemsData && systemLineItemsData && props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
        <span className="selection-count" style={ { position: 'fixed', bottom: '6px', left: '99px' } }>{ selected.length ? `Selected Rows: ${selected.length}`: null}</span>
      </LineItemsTable_s>
      }
    </div>
  )
}

export default LineItemsTable
